import React, { useState, useEffect, useCallback } from 'react';
import { getSalesHeaders as getHeaders } from '../../utils/salesAuth';

const API = process.env.REACT_APP_BACKEND_URL;
function fmt(v) { return v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '$0'; }

const SCORE_COLORS = {
  NONE: { bg: '#f1f5f9', color: '#64748b' },
  LOW: { bg: '#fef9c3', color: '#a16207' },
  MEDIUM: { bg: '#dbeafe', color: '#2563eb' },
  HIGH: { bg: '#dcfce7', color: '#16a34a' },
  MAJOR_PROJECT: { bg: '#fce7f3', color: '#be185d' },
};

export default function ExpansionAnalytics({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sales/expansion/analytics`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading analytics...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Failed to load analytics</div>;

  const lift = data.average_ticket_lift || {};
  const scoreDist = data.score_distribution || {};
  const topServices = data.top_services || [];
  const highValueOpps = data.high_value_opportunities || [];
  const feedbackSummary = data.feedback_summary || {};

  return (
    <div data-testid="expansion-analytics" style={{ padding: '24px 28px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 18px' }}>Expansion Analytics</h1>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        <KPICard label="Opportunities Analyzed" value={data.total_analyzed} color="#1e293b" />
        <KPICard label="Total Recommendations" value={data.total_recommendations} color="#2563eb" />
        <KPICard label="Avg Original Value" value={fmt(lift.avg_original_value)} color="#64748b" />
        <KPICard label="Avg Quoted Value" value={fmt(lift.avg_quoted_value)} color="#16a34a" />
        <KPICard label="Avg Won Value" value={fmt(lift.avg_won_value)} color="#059669" />
      </div>

      {/* Average Ticket Lift — Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 12, padding: '20px 24px', marginBottom: 20, color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Average Ticket Lift</div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {lift.lift_amount > 0 ? `+${fmt(lift.lift_amount)}` : 'Awaiting data'}
            </div>
            {lift.lift_percent > 0 && (
              <div style={{ fontSize: 14, fontWeight: 600, color: '#86efac', marginTop: 2 }}>+{lift.lift_percent}% increase</div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>Original → Quoted</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{fmt(lift.avg_original_value)} → {fmt(lift.avg_quoted_value)}</div>
            {lift.avg_won_value > 0 && (
              <div style={{ fontSize: 12, color: '#86efac', marginTop: 2 }}>Won avg: {fmt(lift.avg_won_value)}</div>
            )}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Score Distribution */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Expansion Score Distribution</h3>
          {['MAJOR_PROJECT', 'HIGH', 'MEDIUM', 'LOW', 'NONE'].map(score => {
            const count = scoreDist[score] || 0;
            const total = data.total_analyzed || 1;
            const pct = Math.round((count / total) * 100);
            const sc = SCORE_COLORS[score];
            return (
              <div key={score} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ width: 100, fontSize: 11, fontWeight: 700, color: sc.color }}>{score.replace('_', ' ')}</span>
                <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.max(pct, 2)}%`, height: '100%', background: sc.color, borderRadius: 4, transition: 'width .3s' }} />
                </div>
                <span style={{ width: 40, fontSize: 11, fontWeight: 600, color: '#475569', textAlign: 'right' }}>{count} ({pct}%)</span>
              </div>
            );
          })}
        </div>

        {/* Feedback Summary */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Salesperson Feedback</h3>
          {Object.keys(feedbackSummary).length === 0 ? (
            <p style={{ fontSize: 12, color: '#94a3b8' }}>No feedback recorded yet</p>
          ) : (
            [
              { key: 'useful', label: 'Useful', color: '#16a34a' },
              { key: 'added_to_scope', label: 'Added to Scope', color: '#059669' },
              { key: 'future_project', label: 'Future Project', color: '#2563eb' },
              { key: 'already_discussed', label: 'Already Discussed', color: '#64748b' },
              { key: 'not_interested', label: 'Not Interested', color: '#f59e0b' },
              { key: 'not_relevant', label: 'Not Relevant', color: '#dc2626' },
            ].filter(f => feedbackSummary[f.key]).map(f => {
              const count = feedbackSummary[f.key] || 0;
              const total = Object.values(feedbackSummary).reduce((s, v) => s + v, 0) || 1;
              return (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 110, fontSize: 11, fontWeight: 600, color: f.color }}>{f.label}</span>
                  <div style={{ flex: 1, height: 16, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.max(Math.round((count / total) * 100), 3)}%`, height: '100%', background: f.color, borderRadius: 4, opacity: 0.7 }} />
                  </div>
                  <span style={{ width: 30, fontSize: 11, fontWeight: 600, color: '#475569', textAlign: 'right' }}>{count}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Top Cross-Sell Services */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Top Cross-Sell Services</h3>
        {topServices.length === 0 ? (
          <p style={{ fontSize: 12, color: '#94a3b8' }}>No recommendations yet</p>
        ) : (
          <div style={{ overflow: 'hidden', borderRadius: 6, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 70px 80px 80px', gap: 8, padding: '8px 14px', background: '#f8fafc', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              <span>Service</span><span>Recommended</span><span>Useful</span><span>Added</span><span>Not Relevant</span>
            </div>
            {topServices.map((s, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 70px 80px 80px', gap: 8, padding: '8px 14px', borderTop: '1px solid #f1f5f9', fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{s.service}</span>
                <span style={{ color: '#475569', fontWeight: 700 }}>{s.recommended}</span>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>{s.useful || 0}</span>
                <span style={{ color: '#059669', fontWeight: 600 }}>{s.added_to_scope || 0}</span>
                <span style={{ color: '#dc2626', fontWeight: 600 }}>{s.not_relevant || 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* High-Value Opportunities */}
      {highValueOpps.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#be185d', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#be185d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            High-Value Expansion Opportunities
          </h3>
          {highValueOpps.map((opp, i) => {
            const sc = SCORE_COLORS[opp.expansion_score] || SCORE_COLORS.HIGH;
            return (
              <div key={i} data-testid={`hv-opp-${opp.opportunity_id}`}
                onClick={() => navigate && navigate(`/sales/opportunity/${opp.opportunity_id}`)}
                style={{ padding: '10px 12px', background: '#fdf2f8', borderRadius: 8, border: '1px solid #fce7f3', marginBottom: 8, cursor: navigate ? 'pointer' : 'default' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{opp.customer_name || 'Unknown'}</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.color }}>{opp.expansion_score.replace('_', ' ')}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>{opp.stage}</span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{opp.title}</div>
                <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                  {opp.estimated_value && <span>Est: <strong>{fmt(opp.estimated_value)}</strong></span>}
                  {opp.quoted_value && <span>Quoted: <strong style={{ color: '#16a34a' }}>{fmt(opp.quoted_value)}</strong></span>}
                  <span style={{ color: '#94a3b8' }}>{opp.recommendations_count} recs</span>
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{opp.score_reason}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KPICard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
